import { useState, useEffect } from "react";
import { puedeCrear } from "../auth/permisos";
import { administraUsuarios } from "../auth/secciones";
import ConfigRotacionArea, { resumenRotacion } from "./ConfigRotacionArea";
import RolesConPermisos from "./RolesConPermisos";
import UsuariosPorRol from "./UsuariosPorRol";
import { listarCuentas, listarRoles } from "./cuentasService";
import { directorioMiembros, eliminar as eliminarFila, guardar as guardarFila, listar, unir } from "../../services/datos";
import { listaPuestos } from "./ConfigRotacionArea";

import { avisar, confirmar } from "../../shared/avisos";
import Selector from "../../shared/components/Selector";

function Modal({ titulo, onCerrar, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/80 p-4 backdrop-blur-sm ventana-fondo">
      <div className="w-full max-w-md max-h-[90vh] overflow-y-auto rounded-2xl border border-white/10 bg-zinc-950 p-6 sm:p-8 ventana-panel">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-black uppercase tracking-tight text-white">{titulo}</h3>
          <button onClick={onCerrar} className="text-gray-400 hover:text-white text-xl">✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

// El formulario escribe los puestos separados por comas; la base los guarda como lista (sin repetidos).
export function datosDeArea(form) {
  const vistos = new Set();
  const puestos = listaPuestos(form.puestos_rotacion)
    .map((p) => p.slice(0, 100))
    .filter((p) => !vistos.has(p.toLowerCase()) && vistos.add(p.toLowerCase()));
  const personas = Number(form.personas_por_reunion);
  return {
    ...form,
    puestos_rotacion: puestos,
    personas_por_reunion: Number.isInteger(personas) && personas > 0 ? personas : 1,
    tipos_servicio_rotacion: [...new Set((form.tipos_servicio_rotacion || []).map(Number))],
    rotacion_automatica: Boolean(form.rotacion_automatica),
    es_infantil: Boolean(form.es_infantil),
  };
}

const PESTANAS = [
  { id: "usuarios", label: "Usuarios", soloAdministradores: true },
  { id: "roles", label: "Roles", soloAdministradores: true },
  { id: "tipos_servicio", label: "Tipos de Servicio" },
  { id: "areas_servicio", label: "Áreas de Servicio" },
];

function Configuracion({ usuario }) {
  // Usuarios y Roles solo los ven los administradores; los pastores, lo demás.
  const esAdministrador = administraUsuarios(usuario);
  const pestanas = PESTANAS.filter((t) => esAdministrador || !t.soloAdministradores);
  const [tab, setTab] = useState(pestanas[0].id);
  const [usuarios, setUsuarios] = useState([]);
  const [roles, setRoles] = useState([]);
  const [tiposServicio, setTiposServicio] = useState([]);
  const [areasServicio, setAreasServicio] = useState([]);
  const [miembros, setMiembros] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});

  const input = "campo";
  const label = "etiqueta";

  // Solo al abrir la pantalla (cargarTodo cambia en cada dibujo).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { cargarTodo(); }, []);

  const cargarTodo = () => {
    if (esAdministrador) {
      // Usuarios y Roles ya están en Supabase (Fase 1).
      listarCuentas().then(setUsuarios).catch((e) => avisar(e.message, "error"));
      listarRoles().then(setRoles).catch((e) => avisar(e.message, "error"));
    }
    const fallo = (e) => avisar(e.message, "error");
    listar("tipos_servicio", { orden: "nombre" }).then(setTiposServicio).catch(fallo);
    Promise.all([listar("areas_servicio", { orden: "nombre" }), directorioMiembros()])
      .then(([areas, personas]) => {
        setMiembros(personas);
        // El líder de cada área, con su nombre.
        setAreasServicio(unir(areas, "lider_id", personas, "miembros"));
      })
      .catch(fallo);
  };

  const abrirModal = (tipo, item = {}) => {
    setModal(tipo);
    setForm(item);
  };

  const cerrarModal = () => {
    setModal(null);
    setForm({});
  };

  const guardar = async (endpoint, id) => {
    try {
      const datos = endpoint === "areas_servicio" ? datosDeArea(form) : form;
      await guardarFila(endpoint, id, datos);
      cargarTodo();
      cerrarModal();
    } catch (error) {
      avisar(error.message || "No se pudo guardar", "error");
    }
  };

  const eliminar = async (endpoint, item, nombre) => {
    if ((await confirmar(`¿Eliminar "${nombre}"?`, { aceptar: "Eliminar" }))) {
      eliminarFila(endpoint, item.id)
        .then(() => cargarTodo())
        .catch((e) => avisar(e.message, "error"));
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="titulo-pagina">Configuración</h2>
        <p className="text-gray-400 text-sm mt-1">{esAdministrador ? "Administra usuarios, roles y configuración del sistema" : "Tipos de reunión y áreas de servicio"}</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 flex-wrap">
        {pestanas.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`pestana ${tab === t.id ? "pestana-activa" : ""}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Usuarios agrupados por rol */}
      {tab === "usuarios" && esAdministrador && <UsuariosPorRol usuarios={usuarios} roles={roles} yoId={usuario?.id} onCambio={cargarTodo} />}

      {/* Roles con sus permisos */}
      {tab === "roles" && esAdministrador && <RolesConPermisos roles={roles} usuarios={usuarios} onCambio={cargarTodo} />}

      {/* Tipos de Servicio */}
      {tab === "tipos_servicio" && (
        <div className="panel p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="titulo-panel">Tipos de Servicio</h3>
            {puedeCrear(usuario) ? (
              <button onClick={() => abrirModal("tipo_servicio")} className="btn-primario btn-sm">
                Agregar
              </button>
            ) : (
              <span className="text-sm text-gray-400">Solo pastor o administrador puede crear</span>
            )}
          </div>
          <div className="space-y-3">
            {tiposServicio.map((t) => (
              <div key={t.id} className="panel-interior flex items-center justify-between p-4 transition hover:border-white/30">
                <div>
                  <p className="text-white font-semibold">{t.nombre}</p>
                  <p className="text-gray-400 text-xs mt-1">{t.frecuencia} · {t.dirigido_a}</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => abrirModal("tipo_servicio", t)} className="btn-fantasma btn-sm">Editar</button>
                  <button onClick={() => eliminar("tipos_servicio", t, t.nombre)} className="btn-peligro btn-sm">Eliminar</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Áreas de Servicio */}
      {tab === "areas_servicio" && (
        <div className="panel p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="titulo-panel">Áreas de Servicio</h3>
            {puedeCrear(usuario) ? (
              <button onClick={() => abrirModal("area_servicio")} className="btn-primario btn-sm">
                Agregar
              </button>
            ) : (
              <span className="text-sm text-gray-400">Solo pastor o administrador puede crear</span>
            )}
          </div>
          <div className="space-y-3">
            {areasServicio.map((a) => (
              <div key={a.id} className="panel-interior flex items-center justify-between p-4 transition hover:border-white/30">
                <div>
                  <p className="text-white font-semibold">{a.nombre}</p>
                  <p className="text-gray-400 text-xs mt-1">
                    {a.descripcion || "Sin descripción"} · Líder: {a.miembros ? `${a.miembros.nombres} ${a.miembros.apellidos}` : "Sin asignar"}
                  </p>
                  {resumenRotacion(a, tiposServicio) && (
                    <p className="text-sky-300 text-xs mt-1">{resumenRotacion(a, tiposServicio)}</p>
                  )}
                </div>
                <div className="flex gap-2">
                  <button onClick={() => abrirModal("area_servicio", a)} className="btn-fantasma btn-sm">Editar</button>
                  <button onClick={() => eliminar("areas_servicio", a, a.nombre)} className="btn-peligro btn-sm">Eliminar</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modal Tipo Servicio */}
      {modal === "tipo_servicio" && (
        <Modal titulo={form.id ? "Editar Tipo" : "Nuevo Tipo de Servicio"} onCerrar={cerrarModal}>
          <div className="space-y-4">
            <div>
              <label className={label}>Nombre *</label>
              <input value={form.nombre || ""} onChange={(e) => setForm({ ...form, nombre: e.target.value })} className={input} placeholder="Nombre del servicio" />
            </div>
            <div>
              <label className={label}>Descripción</label>
              <textarea value={form.descripcion || ""} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} className={input} rows={2} placeholder="Descripción" />
            </div>
            <div>
              <label className={label}>Frecuencia</label>
              <Selector value={form.frecuencia || "semanal"} onChange={(e) => setForm({ ...form, frecuencia: e.target.value })} className={input}>
                <option value="semanal">Semanal</option>
                <option value="mensual">Mensual</option>
                <option value="ocasional">Ocasional</option>
              </Selector>
            </div>
            <div>
              <label className={label}>Dirigido a</label>
              <Selector value={form.dirigido_a || "todos"} onChange={(e) => setForm({ ...form, dirigido_a: e.target.value })} className={input}>
                <option value="todos">Todos</option>
                <option value="adultos">Adultos</option>
                <option value="jovenes">Jóvenes</option>
                <option value="ninos">Niños</option>
                <option value="hombres">Hombres</option>
                <option value="damas">Damas</option>
              </Selector>
            </div>
          </div>
          <div className="flex gap-3 mt-6">
            <button onClick={() => guardar("tipos_servicio", form.id)} className="flex-1 btn-primario">Guardar</button>
            <button onClick={cerrarModal} className="flex-1 btn-fantasma">Cancelar</button>
          </div>
        </Modal>
      )}

      {/* Modal Área Servicio */}
      {modal === "area_servicio" && (
        <Modal titulo={form.id ? "Editar Área" : "Nueva Área de Servicio"} onCerrar={cerrarModal}>
          <div className="space-y-4">
            <div>
              <label className={label}>Nombre *</label>
              <input value={form.nombre || ""} onChange={(e) => setForm({ ...form, nombre: e.target.value })} className={input} placeholder="Nombre del área" />
            </div>
            <div>
              <label className={label}>Descripción</label>
              <textarea value={form.descripcion || ""} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} className={input} rows={2} placeholder="Descripción" />
            </div>
            <div>
              <label className={label}>Líder</label>
              <Selector value={form.lider_id || ""} onChange={(e) => setForm({ ...form, lider_id: e.target.value })} className={input}>
                <option value="">Sin asignar</option>
                {miembros.map((m) => (
                  <option key={m.id} value={m.id}>{m.nombres} {m.apellidos}</option>
                ))}
              </Selector>
            </div>
            <label className="flex items-start gap-3">
              <input type="checkbox" checked={Boolean(form.es_infantil)} onChange={(e) => setForm({ ...form, es_infantil: e.target.checked })} className="mt-1 h-4 w-4" />
              <span className="text-sm text-white">
                Es de Mi Casa Kids
                <span className="block text-xs text-gray-400">La gestiona quien tiene "Programación Kids" y usa la rotación de profesores.</span>
              </span>
            </label>
            <ConfigRotacionArea form={form} setForm={setForm} tiposServicio={tiposServicio} input={input} label={label} />
          </div>
          <div className="flex gap-3 mt-6">
            <button onClick={() => guardar("areas_servicio", form.id)} className="flex-1 btn-primario">Guardar</button>
            <button onClick={cerrarModal} className="flex-1 btn-fantasma">Cancelar</button>
          </div>
        </Modal>
      )}

    </div>
  );
}

export default Configuracion;
